import styles from './styles.module.scss'
import CollectionContext from '@components/collection/collectionContext'
import { useContext } from 'react'
import { FilterOption } from 'types/filters'
import { ReactComponent as FilterIcon } from '@svgs/keyline/filter.svg'
import {
  ChildFilterValue,
  FilterBuild,
  FilterBuildItem,
  FilterBuildItemValue,
  ParentFilterValue
} from '../types'
import useIsReactClient from '@helpers/hooks/useIsReactClient'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { useFitFinderToggle } from '@components/FitFinder/hooks/useFitFinderToggle'
import { ButtonIcon } from '@components/ui/Button'
import { useFit } from '@helpers/hooks/useFit'

function isParentFilterValue(
  item: FilterBuildItemValue
): item is ParentFilterValue {
  return 'values' in item && Array.isArray(item.values)
}

function doesItemMatchFilters(
  item: FilterBuildItemValue,
  filterOptions: FilterOption[]
): boolean {
  return filterOptions.some(
    filter =>
      item.path?.toLowerCase() === filter.type.toLowerCase() &&
      isParentFilterValue(item) &&
      item.values.some(
        value => (value as ChildFilterValue).value === filter.value
      )
  )
}

function countSelectedChildWithFilter(
  filterBuild: FilterBuild,
  filterOptions: FilterOption[]
) {
  let count = 0

  function traverseValues(values: FilterBuildItem['values'] | undefined): void {
    if (!Array.isArray(values)) return

    for (const item of values) {
      if (doesItemMatchFilters(item, filterOptions)) {
        count++
      }

      if (isParentFilterValue(item)) {
        traverseValues(item.values)
      }
    }
  }

  filterBuild.forEach(item => traverseValues(item.values))

  return count
}

function FilterToggleButton({
  totalResults,
  filterBuild,
  isApplyFitActive
}: {
  totalResults: number
  filterBuild: FilterBuild
  isApplyFitActive: boolean
}) {
  const { savedFit, filters } = useFit()
  const { openFilters } = useContext(CollectionContext)
  const { openFitFinder } = useFitFinderToggle()
  const isReactClient = useIsReactClient()
  const filteredBrowsingFitOptions = filters
  const total = countSelectedChildWithFilter(
    filterBuild,
    filteredBrowsingFitOptions
  )
  const filterOptionsTotalValue = total > 0 ? ` (${total})` : ''

  return (
    <div className={styles.filterContainer}>
      <>
        <p className={styles.filtersResults}>
          {totalResults} styles available{' '}
          {isReactClient && (
            <>
              {savedFit && isApplyFitActive ? 'in ' : ''}
              {savedFit && isApplyFitActive && (
                <ButtonMinimal
                  onClick={openFitFinder}
                  variant="success"
                  underlined={true}
                  textSize="regular"
                >
                  Your fit
                </ButtonMinimal>
              )}
            </>
          )}
        </p>

        <ButtonMinimal onClick={openFilters} classes={styles.filterButtonIcon}>
          <ButtonIcon>
            <FilterIcon aria-label="filter icon" fill="#001e3c" />
          </ButtonIcon>{' '}
          Filter {filterOptionsTotalValue}
        </ButtonMinimal>
      </>
    </div>
  )
}

export default FilterToggleButton
